import React, { useEffect, useState } from "react";
import { notificationService } from "@/services/NotificationService";
import { notificationPollingService } from "@/services/NotificationPollingService";
import { wakeLockService } from "@/services/WakeLockService";
import { Wifi, WifiOff, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

interface ConnectionStatus {
  isConnected: boolean;
  isHealthy: boolean;
  isPollingActive: boolean;
  wakeLockActive: boolean;
  lastActivity: number;
  attempts: number;
}

export const ConnectionStatusIndicator: React.FC = () => {
  const [status, setStatus] = useState<ConnectionStatus>({
    isConnected: false,
    isHealthy: false,
    isPollingActive: false,
    wakeLockActive: false,
    lastActivity: 0,
    attempts: 0,
  });
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const updateStatus = () => {
      const notificationStatus = notificationService.getConnectionStatus();
      const pollingStatus = notificationPollingService.getStatus();
      const wakeLockStatus = wakeLockService.getStatus();

      setStatus({
        isConnected: notificationStatus.connected,
        isHealthy: notificationStatus.isServerConnectionAlive,
        isPollingActive: pollingStatus.active,
        wakeLockActive: wakeLockStatus.active,
        lastActivity: notificationStatus.lastActivityTime,
        attempts: notificationStatus.attempts,
      });
    };

    // Update immediately
    updateStatus();

    // Update every 5 seconds
    const interval = setInterval(updateStatus, 5000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = () => {
    if (!status.isConnected && !status.isPollingActive) {
      return "text-red-500";
    }
    if (!status.isHealthy || status.isPollingActive) {
      return "text-yellow-500";
    }
    return "text-green-500";
  };

  const getStatusIcon = () => {
    if (!status.isConnected && !status.isPollingActive) {
      return <WifiOff className="w-4 h-4" />;
    }
    if (!status.isHealthy || status.isPollingActive) {
      return <AlertTriangle className="w-4 h-4" />;
    }
    return <Wifi className="w-4 h-4" />;
  };

  const getStatusText = () => {
    if (!status.isConnected && !status.isPollingActive) {
      return "Déconnecté";
    }
    if (status.isPollingActive) {
      return "Mode dégradé";
    }
    if (!status.isHealthy) {
      return "Connexion instable";
    }
    return "Connecté";
  };

  const getTimeSinceLastActivity = () => {
    if (!status.lastActivity) return "N/A";
    const seconds = Math.floor((Date.now() - status.lastActivity) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h`;
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={cn(
          "flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors",
          "hover:bg-gray-100 dark:hover:bg-gray-800",
          getStatusColor()
        )}
        title="Statut de la connexion"
      >
        {getStatusIcon()}
        <span className="text-xs font-medium">{getStatusText()}</span>
      </button>

      {showDetails && (
        <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 z-50">
          <h3 className="font-semibold text-sm mb-3">Détails de connexion</h3>

          <div className="space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Mercure:</span>
              <span
                className={
                  status.isConnected ? "text-green-500" : "text-red-500"
                }
              >
                {status.isConnected ? "Connecté" : "Déconnecté"}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-500">État:</span>
              <span
                className={
                  status.isHealthy ? "text-green-500" : "text-yellow-500"
                }
              >
                {status.isHealthy ? "Sain" : "Dégradé"}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-500">Polling:</span>
              <span
                className={
                  status.isPollingActive ? "text-yellow-500" : "text-gray-400"
                }
              >
                {status.isPollingActive ? "Actif" : "Inactif"}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-500">Wake Lock:</span>
              <span
                className={
                  status.wakeLockActive ? "text-green-500" : "text-gray-400"
                }
              >
                {status.wakeLockActive ? "Actif" : "Inactif"}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-500">Dernière activité:</span>
              <span>{getTimeSinceLastActivity()}</span>
            </div>

            {status.attempts > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">Tentatives:</span>
                <span className="text-yellow-500">{status.attempts}</span>
              </div>
            )}
          </div>

          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                notificationService.forceReset();
                setShowDetails(false);
              }}
              className="w-full text-xs bg-blue-500 text-white rounded px-3 py-1.5 hover:bg-blue-600 transition-colors"
            >
              Forcer la reconnexion
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
